// 2026-06-21 (user request): Dialog export WO dengan PILIHAN KOLOM.
// Saat klik Export Excel / Print PDF → popup checklist data apa saja yang
// ditampilkan (bukan hanya kolom tabel di layar): identitas, tahap & waktu,
// SPH/Nego per pengajuan, keuangan (DPP/PPN/PPh/retensi/dst), dan lainnya.
// Bawaan = 10 kolom tabel saat ini; tombol "Pilih Semua" & "Bawaan Tabel".
import { useEffect, useMemo, useState } from "react";
import { X, FileSpreadsheet, Printer } from "lucide-react";
import { WO_EXPORT_FIELDS, WO_EXPORT_DEFAULT_KEYS, sphFieldOptions } from "@/lib/woExport";

const GROUP_ORDER = ["Identitas", "Tahap & Waktu", "SPH & Negosiasi", "Keuangan", "Lainnya"];

export default function WOExportDialog({
  open, mode, filteredCount, selectedCount, maxSph, onClose, onConfirm,
}) {
  const [checked, setChecked] = useState(() => new Set(WO_EXPORT_DEFAULT_KEYS));
  const [scope, setScope] = useState("all");

  // Reset ke bawaan setiap dialog dibuka (mode berpindah excel/pdf juga reset).
  useEffect(() => {
    if (open) {
      setChecked(new Set(WO_EXPORT_DEFAULT_KEYS));
      setScope("all");
    }
  }, [open, mode]);

  // Katalog + opsi SPH dinamis (jumlah kolom mengikuti data terbanyak).
  const catalog = useMemo(() => {
    const byGroup = new Map();
    for (const f of WO_EXPORT_FIELDS) {
      if (!byGroup.has(f.group)) byGroup.set(f.group, []);
      byGroup.get(f.group).push(f);
      if (f.key === "sph_count") {
        for (const o of sphFieldOptions(maxSph)) byGroup.get(f.group).push(o);
      }
    }
    return GROUP_ORDER.filter((g) => byGroup.has(g)).map((g) => ({ group: g, fields: byGroup.get(g) }));
  }, [maxSph]);

  if (!open) return null;

  const isPdf = mode === "pdf";
  const modeLabel = isPdf ? "Cetak PDF" : "Export Excel";
  const toggle = (k) => setChecked((s) => {
    const n = new Set(s);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  const confirm = () => onConfirm({ keys: WO_EXPORT_FIELDS.length ? [...checked] : [], scope });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4"
      data-testid="wmd-export-dialog"
      onClick={onClose}
    >
      <div
        className="bg-white border border-slate-200 shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-base font-bold text-[#0B1D3A]" data-testid="wmd-export-dialog-title">
              {modeLabel} Daftar Working Order
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Centang data yang ingin ditampilkan. Urutan baris dikelompokkan per No. PO (PO sama berdekatan).
            </p>
          </div>
          <button type="button" onClick={onClose} data-testid="wmd-export-dialog-close"
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>

        {/* Cakupan data */}
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-4 flex-wrap shrink-0" data-testid="wmd-export-scope">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Cakupan:</span>
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
            <input type="radio" name="wmd-export-scope" checked={scope === "all"} onChange={() => setScope("all")}
              data-testid="wmd-export-scope-all" className="accent-[#0B1D3A]" />
            Semua hasil filter ({filteredCount} WO)
          </label>
          {selectedCount > 0 && (
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
              <input type="radio" name="wmd-export-scope" checked={scope === "selected"} onChange={() => setScope("selected")}
                data-testid="wmd-export-scope-selected" className="accent-[#0B1D3A]" />
              Hanya WO terpilih ({selectedCount} WO)
            </label>
          )}
          <span className="ml-auto flex items-center gap-3">
            <button type="button" onClick={() => {
              const all = new Set(WO_EXPORT_DEFAULT_KEYS);
              catalog.forEach((g) => g.fields.forEach((f) => all.add(f.key)));
              setChecked(all);
            }} data-testid="wmd-export-all-fields"
              className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-[#FF5A1F]">
              Pilih Semua
            </button>
            <button type="button" onClick={() => setChecked(new Set(WO_EXPORT_DEFAULT_KEYS))}
              data-testid="wmd-export-default-fields"
              className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-[#FF5A1F]">
              Bawaan Tabel
            </button>
          </span>
        </div>

        {/* Checklist kolom bergrup */}
        <div className="px-5 py-4 overflow-y-auto grow" data-testid="wmd-export-fields">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            {catalog.map((g) => (
              <div key={g.group}>
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#0B1D3A] border-b border-slate-200 pb-1 mb-2">
                  {g.group}
                </div>
                <div className="space-y-1.5">
                  {g.fields.map((f) => (
                    <label key={f.key} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer hover:text-[#0B1D3A]">
                      <input type="checkbox" checked={checked.has(f.key)} onChange={() => toggle(f.key)}
                        data-testid={`wmd-export-field-${f.key}`} className="accent-[#0B1D3A]" />
                      {f.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex flex-wrap gap-2 justify-end items-center bg-slate-50 shrink-0">
          <span className="text-[10px] text-slate-500 tabular-nums mr-auto" data-testid="wmd-export-col-count">
            {checked.size} kolom dipilih
          </span>
          <button type="button" data-testid="wmd-export-dialog-cancel" onClick={onClose}
            className="px-3 py-2 text-xs uppercase tracking-wider font-semibold border border-slate-300 text-slate-700 hover:bg-white transition-colors">
            Batal
          </button>
          <button
            type="button"
            data-testid="wmd-export-confirm"
            disabled={checked.size === 0}
            onClick={confirm}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${isPdf
              ? "border-rose-600 bg-rose-600 text-white hover:bg-rose-700"
              : "border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700"}`}
          >
            {isPdf ? <Printer size={13} /> : <FileSpreadsheet size={13} />}
            {modeLabel} ({scope === "selected" ? selectedCount : filteredCount} WO)
          </button>
        </div>
      </div>
    </div>
  );
}
